import React from "react";
import {Accordion, FlexBox, FlexColumn, ListItem, UnorderedList} from "scuid-x";
import styled from "styled-components";
import DLBack from "../assets/images/dl-back-example.svg";
import DLFront from "../assets/images/dl-front-example.svg";
import GreenCardBack from "../assets/images/green-card-back-example.png";
import GreenCardFront from "../assets/images/green-card-front-example.png";
import Passport from "../assets/images/passport-example.png";
import { DocType } from "../constants/DocType";
import "./ManualUpload.css";
import {ManualUploadHeader} from "./ManualUploadHeader";

const DocExample = styled(Accordion)`
  font-family: "TT Norms", proxima-nova, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif;
  font-size: 0.875rem;
  rgb(23, 113, 166);
  font-weight: 400;
  background-color: #FFFFFF;
  border-bottom: none;
  margin-bottom: 0;
  text-align: right;
  padding-left:1.25em;
  padding-right:1.25em;

  img {
    margin-top: 10px;
    max-width: 17rem;
  }
 `;

const StyledListItem = styled(ListItem)`
    font-family: "TT Norms", proxima-nova, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif;
    font-size: 0.875rem;
    color: rgb(110, 110, 110);
    font-weight: 400;
    text-align: left;
 `;

const StyledFlexBox = styled(FlexBox)`
  justify-content: center;
 `;

const StyledFlexColumn = styled(FlexColumn)`
  max-width: 24rem;
`;

const StyledH3 = styled.header`
  font-family: Larsseit, "TT Norms", proxima-nova, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  color: rgb(38, 38, 38);
  padding-top: 1rem;
  text-align: left;
`;

export interface ManualUploadExampleProps {
  title: string;
  description: string;
  docType: DocType;
}

export const DriverLicenseManualUploadExample: React.SFC<ManualUploadExampleProps> = (props) => {
  const image = props.docType === DocType.LICENSE_FRONT ?
    <img src={DLFront} alt="driver license front"/> :
    <img src={DLBack} alt="driver license back"/>;

  return (
    <ManualUploadHeader
      title={props.title}
      description={props.description}
    >
      <DocExample title={<a className="ManualUpload-dropDown">Show example</a>}>
        <StyledFlexBox direction={"row"} justifyContent={"flex-start"} spacingH={"0.625rem"}>
          <FlexColumn>
            {image}
          </FlexColumn>
          <StyledFlexColumn>
            <StyledH3>Driver's License Example</StyledH3>
            <UnorderedList>
              <StyledListItem>Image must be clear and in focus</StyledListItem>
              <StyledListItem>Your ID must be valid and current (cannot be expired)</StyledListItem>
              <StyledListItem>Image must be in full color</StyledListItem>
            </UnorderedList>
          </StyledFlexColumn>
        </StyledFlexBox>
      </DocExample>
    </ManualUploadHeader>
  );
};

export const PassportManualUploadExample: React.SFC<ManualUploadExampleProps> = (props) => {
  const image = <img src={Passport} alt="passport"/>;

  return (
      <ManualUploadHeader
          title={props.title}
          description={props.description}
      >
        <DocExample title={<a className="ManualUpload-dropDown">Show example</a>}>
          <StyledFlexBox direction={"row"} justifyContent={"flex-start"} spacingH={"0.625rem"}>
            <FlexColumn>
              {image}
            </FlexColumn>
            <StyledFlexColumn>
              <StyledH3>US Passport Example</StyledH3>
              <UnorderedList>
                <StyledListItem>
                  Image of primary two pages of passport with picture, name, and signature shown
                </StyledListItem>
                <StyledListItem>Clear and in focus (characters must be readable)</StyledListItem>
                <StyledListItem>Valid and current (document must not be expired)</StyledListItem>
                <StyledListItem>Color</StyledListItem>
                <StyledListItem>Signed</StyledListItem>
              </UnorderedList>
            </StyledFlexColumn>
          </StyledFlexBox>
        </DocExample>
      </ManualUploadHeader>
  );
};

export const StateIdManualUploadExample: React.SFC<ManualUploadExampleProps> = (props) => {
  const image = props.docType === DocType.STATE_ID_FRONT ?
      <img src={DLFront} alt="state id front"/> :
      <img src={DLBack} alt="state id back"/>;

  return (
      <ManualUploadHeader
          title={props.title}
          description={props.description}
      >
        <DocExample title={<a className="ManualUpload-dropDown">Show example</a>}>
          <StyledFlexBox direction={"row"} justifyContent={"flex-start"} spacingH={"0.625rem"}>
            <FlexColumn>
              {image}
            </FlexColumn>
            <StyledFlexColumn>
              <StyledH3>State ID Example</StyledH3>
              <UnorderedList>
                <StyledListItem>Image must be clear and in focus</StyledListItem>
                <StyledListItem>Your ID must be valid and current (cannot be expired)</StyledListItem>
                <StyledListItem>Image must be in full color</StyledListItem>
              </UnorderedList>
            </StyledFlexColumn>
          </StyledFlexBox>
        </DocExample>
      </ManualUploadHeader>
  );
};

export const GreenCardManualUploadExample: React.SFC<ManualUploadExampleProps> = (props) => {
  const image = props.docType === DocType.GREEN_CARD_FRONT ?
      <img src={GreenCardFront} alt="green card front"/> :
      <img src={GreenCardBack} alt="green card back"/>;

  return (
      <ManualUploadHeader
          title={props.title}
          description={props.description}
      >
        <DocExample title={<a className="ManualUpload-dropDown">Show example</a>}>
          <StyledFlexBox direction={"row"} justifyContent={"flex-start"} spacingH={"0.625rem"}>
            <FlexColumn>
              {image}
            </FlexColumn>
            <StyledFlexColumn>
              <StyledH3>Green Card Example</StyledH3>
              <UnorderedList>
                <StyledListItem>Image must be clear and in focus</StyledListItem>
                <StyledListItem>Your Green Card must be valid and current (cannot be expired)</StyledListItem>
                <StyledListItem>Image must be in full color</StyledListItem>
              </UnorderedList>
            </StyledFlexColumn>
          </StyledFlexBox>
        </DocExample>
      </ManualUploadHeader>
  );
};


// WEBPACK FOOTER //
// ./src/manual-upload/ManualUploadExample.tsx